"use client";

import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";
import { trpc } from "@/lib/trpc/react";
import { trackReviewSubmission } from "@/lib/services/analytics";

/**
 * Real-time set review hook
 *
 * Modern architecture:
 * - Database is the single source of truth
 * - Real-time events drive all updates
 * - Optimistic updates for instant feedback
 * - No cache invalidation needed (staleTime: Infinity)
 * - Handles both status toggles and full reviews
 */

// Custom events for syncing optimistic updates across components
const SET_REVIEW_EVENT = "set-review-update";
const REVIEW_DELETED_EVENT = "review-deleted";

// Type-safe event interface for review deletion
interface ReviewDeletedDetail {
	setId: string;
	userId: string;
}

export interface SetReview {
	id: string;
	setId: string;
	artistId: string;
	userId: string;
	rating: number;
	content: string | null;
	loved: boolean;
	sawLive: boolean;
	sawStream: boolean;
	wantToSee: boolean;
	mustSee: boolean;
	createdAt: string;
	updatedAt: string;
}

interface UseRealtimeSetReviewOptions {
	setId: string;
	artistId: string;
	userId: string | null;
	enabled?: boolean;
	festivalId?: string;
}

export function useRealtimeSetReview({
	setId,
	artistId,
	userId,
	enabled = true,
	festivalId,
}: UseRealtimeSetReviewOptions) {
	// Local state is our source of truth
	const [review, setReview] = useState<SetReview | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null);
	const instanceId = useRef(Math.random().toString(36).substring(7));
	const supabase = createClient();
	const utils = trpc.useUtils();

	// tRPC mutation for saving reviews
	const saveReviewMutation = trpc.festival.saveSetReview.useMutation({
		onMutate: async (variables) => {
			// Optimistic update for immediate UI feedback
			const isDeleting =
				variables.rating === -2 ||
				(variables.rating === 0 &&
					!variables.content?.trim() &&
					!variables.loved &&
					!variables.wantToSee &&
					!variables.mustSee &&
					!variables.sawLive &&
					!variables.sawStream);

			if (isDeleting) {
				// Optimistically remove the review
				setReview(null);

				// Broadcast deletion to other components for immediate UI updates
				// This ensures the reviews list updates instantly without waiting for real-time
				window.dispatchEvent(
					new CustomEvent<ReviewDeletedDetail>(REVIEW_DELETED_EVENT, {
						detail: { setId: variables.setId, userId: userId! },
					}),
				);
			} else {
				// Optimistically update the review
				setReview({
					id: review?.id || `temp-${setId}`,
					setId: variables.setId,
					artistId: variables.artistId || artistId, // Fallback to hook's artistId
					userId: userId!,
					rating: variables.rating || 0,
					content: variables.content || null,
					loved: variables.loved || false,
					sawLive: variables.sawLive || false,
					sawStream: variables.sawStream || false,
					wantToSee: variables.wantToSee || false,
					mustSee: variables.mustSee || false,
					createdAt: review?.createdAt || new Date().toISOString(),
					updatedAt: new Date().toISOString(),
				});
			}
		},
		onSuccess: async () => {
			const FORCE_REFETCH = { refetchType: 'all' as const };
			await Promise.all([
				utils.festival.getUserSetReview.invalidate({ setId }),
				utils.festival.getCommunitySetReviews.invalidate({ setId }),
				utils.festival.getFacepileData.invalidate(),
				utils.festival.getUserFestivalData.invalidate(),
				utils.festival.getUserSetReviews.invalidate(),
				utils.festival.list.invalidate(),
				utils.festival.getBySlug.invalidate(),
				utils.artist.getStats.invalidate({ artistId }),
				utils.artist.getSets.invalidate(undefined, FORCE_REFETCH),
				utils.artist.getFestivals.invalidate(undefined, FORCE_REFETCH),
				utils.artist.getCounts.invalidate({ artistId }),
				utils.user.getProfileFestivals.invalidate(undefined, FORCE_REFETCH),
				utils.user.getProfileSets.invalidate(undefined, FORCE_REFETCH),
				utils.user.getProfileArtists.invalidate(undefined, FORCE_REFETCH),
			]);
		},
		onError: (err, variables, context) => {
			// On error, revert optimistic update
			// Real-time will correct the state
			const errorMessage = err.message?.includes("UNAUTHORIZED")
				? "Please sign in to save your review"
				: err.message?.includes("Rate limit")
					? err.message
					: "Failed to save review";

			toast.error(errorMessage);
		},
	});

	// Initial data fetch - runs when setId/artistId changes (e.g., switching sets)
	useEffect(() => {
		let cancelled = false;

		if (!userId || !setId || !artistId || !enabled) {
			setIsLoading(false);
			setReview(null);
			return;
		}

		async function fetchInitialReview() {
			if (!userId) return;

			try {
				type SetReviewRow = Database["public"]["Tables"]["set_reviews"]["Row"];

				const { data, error } = await supabase
					.from("set_reviews")
					.select("*")
					.eq("user_id", userId)
					.eq("set_id", setId)
					.eq("artist_id", artistId)
					.maybeSingle() as { data: SetReviewRow | null; error: any };

				if (cancelled) return;

				if (!error && data && data.set_id) {
					setReview({
						id: data.id,
						setId: data.set_id,
						artistId: data.artist_id,
						userId: data.user_id,
						rating: data.rating,
						content: data.content,
						loved: data.loved,
						sawLive: data.saw_live,
						sawStream: data.saw_stream,
						wantToSee: data.want_to_see,
						mustSee: data.must_see,
						createdAt: data.created_at,
						updatedAt: data.updated_at,
					});
				} else {
					setReview(null);
				}
			} catch (error) {
				if (cancelled) return;
				console.error("Error fetching initial set review:", error);
				setReview(null);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		fetchInitialReview();
		return () => { cancelled = true; };
	}, [userId, setId, artistId, enabled, supabase]);

	// Real-time subscription - this is where all updates come from
	useEffect(() => {
		if (!userId || !setId || !artistId || !enabled) return;

		// Unique channel per instance to avoid conflicts
		const channelName = `set-review:${setId}:${artistId}:${userId}:${instanceId.current}`;

		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "set_reviews",
					filter: `user_id=eq.${userId}`,
				},
				(payload: RealtimePostgresChangesPayload<any>) => {
					// Client-side filter for set_id and artist_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if (record?.set_id !== setId || record?.artist_id !== artistId) {
						return;
					}

					// Real-time update from database

					// IMPORTANT: Longer delay to let optimistic updates take precedence
					// This prevents race conditions where real-time events override optimistic updates
					setTimeout(() => {
						if (payload.eventType === "DELETE") {
							setReview(null);
						} else if (payload.new) {
							setReview({
								id: payload.new.id,
								setId: payload.new.set_id,
								artistId: payload.new.artist_id,
								userId: payload.new.user_id,
								rating: payload.new.rating,
								content: payload.new.content,
								loved: payload.new.loved,
								sawLive: payload.new.saw_live,
								sawStream: payload.new.saw_stream,
								wantToSee: payload.new.want_to_see,
								mustSee: payload.new.must_see,
								createdAt: payload.new.created_at,
								updatedAt: payload.new.updated_at,
							});
						}
					}, 1000); // Longer delay to ensure optimistic updates have time to settle
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully
				if (status === "CHANNEL_ERROR") {
					console.warn(
						`[Realtime] Channel error for set review. Will retry on reconnect.`
					);
				} else if (status === "TIMED_OUT") {
					console.warn(
						`[Realtime] Connection timeout for set review. Will retry.`
					);
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [userId, setId, artistId, enabled, supabase]);

	// Listen for optimistic updates from other component instances on the same page
	useEffect(() => {
		if (!userId || !setId || !artistId) {
			return;
		}

		const handleOptimisticUpdate = (event: CustomEvent) => {
			const {
				userId: eventUserId,
				setId: eventSetId,
				artistId: eventArtistId,
				review: eventReview,
			} = event.detail;

			const userMatch = eventUserId === userId;
			const setMatch = eventSetId === setId;
			const artistMatch = eventArtistId === artistId;

			// Check if this event is for our set/artist combination
			if (
				eventUserId === userId &&
				eventSetId === setId &&
				eventArtistId === artistId
			) {
				// Apply optimistic update from management hook
				setReview(eventReview);
			}
		};

		window.addEventListener(
			SET_REVIEW_EVENT as any,
			handleOptimisticUpdate as any,
		);

		return () => {
			window.removeEventListener(
				SET_REVIEW_EVENT as any,
				handleOptimisticUpdate as any,
			);
		};
	}, [userId, setId, artistId]); // Dependencies are correct - no need for review here

	// Ref-based debounce for server mutations — stable across re-renders.
	// The optimistic update + broadcast happen instantly; only the server call is debounced.
	const pendingMutationRef = useRef<any>(null);
	const mutationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const flushMutation = useCallback(async () => {
		const data = pendingMutationRef.current;
		pendingMutationRef.current = null;
		if (!data) return;
		try {
			await saveReviewMutation.mutateAsync(data);

			// Track review submission analytics
			if (festivalId && data.rating > 0) {
				trackReviewSubmission(festivalId, data.rating, "set", setId, !!data.content?.trim());
			}
		} catch {
			// Error handled in mutation onError / realtime will correct
		}
	}, [saveReviewMutation, festivalId, setId]);

	const debounceMutation = useCallback((mutationData: any) => {
		pendingMutationRef.current = mutationData;
		if (mutationTimerRef.current) clearTimeout(mutationTimerRef.current);
		mutationTimerRef.current = setTimeout(flushMutation, 800);
	}, [flushMutation]);

	// Save review with optimistic updates
	const saveReview = async (data: {
		rating?: number;
		content?: string;
		loved?: boolean;
		sawLive?: boolean;
		sawStream?: boolean;
		wantToSee?: boolean;
		mustSee?: boolean;
	}, { immediate = false }: { immediate?: boolean } = {}) => {
		if (!userId) {
			toast.error("Please sign in to save your review");
			return;
		}

		// Merge with existing review data
		const updatedReview = review
			? {
					...review,
					...data,
					updatedAt: new Date().toISOString(),
				}
			: {
					id: `temp-${setId}-${artistId}`,
					setId,
					artistId,
					userId,
					rating: data.rating || 0,
					content: data.content || null,
					loved: data.loved || false,
					sawLive: data.sawLive || false,
					sawStream: data.sawStream || false,
					wantToSee: data.wantToSee || false,
					mustSee: data.mustSee || false,
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
				};

		// Check if this should delete the review (all fields empty)
		const shouldDelete =
			updatedReview.rating === 0 &&
			!updatedReview.content?.trim() &&
			!updatedReview.loved &&
			!updatedReview.sawLive &&
			!updatedReview.sawStream &&
			!updatedReview.wantToSee &&
			!updatedReview.mustSee;

		// 1. Optimistic update - instant UI feedback
		const optimisticReview = shouldDelete ? null : updatedReview;
		setReview(optimisticReview);

		// 2. Broadcast to other instances on the page
		const event = new CustomEvent(SET_REVIEW_EVENT, {
			detail: {
				userId,
				setId,
				artistId,
				review: optimisticReview,
			},
		});
		window.dispatchEvent(event);

		// 3. Send to database (debounced for rapid toggles, immediate for modal saves)
		const mutationData = {
			setId,
			artistId,
			rating: shouldDelete ? 0 : updatedReview.rating,
			content: shouldDelete ? "" : updatedReview.content || "",
			loved: shouldDelete ? false : updatedReview.loved,
			sawLive: shouldDelete ? false : updatedReview.sawLive,
			sawStream: shouldDelete ? false : updatedReview.sawStream,
			wantToSee: shouldDelete ? false : updatedReview.wantToSee,
			mustSee: shouldDelete ? false : updatedReview.mustSee,
		};

		if (immediate) {
			// Flush any pending debounced mutation first, then send immediately
			if (mutationTimerRef.current) {
				clearTimeout(mutationTimerRef.current);
				pendingMutationRef.current = null;
			}
			try {
				await saveReviewMutation.mutateAsync(mutationData);
				if (festivalId && !shouldDelete && updatedReview.rating > 0) {
					trackReviewSubmission(festivalId, updatedReview.rating, "set", setId, !!updatedReview.content?.trim());
				}
			} catch {
				// Error handled in mutation onError
			}
		} else {
			debounceMutation(mutationData);
		}

		// 4. Real-time will confirm/correct
	};

	// Toggle a single status flag (debounced — only the last toggle fires a mutation)
	const toggleStatus = async (
		status: "wantToSee" | "mustSee" | "sawLive" | "sawStream",
		value?: boolean,
	) => {
		const currentValue = review?.[status] || false;
		const newValue = value !== undefined ? value : !currentValue;

		await saveReview({
			rating: review?.rating,
			content: review?.content || undefined,
			loved: review?.loved,
			sawLive: review?.sawLive,
			sawStream: review?.sawStream,
			wantToSee: review?.wantToSee,
			mustSee: review?.mustSee,
			[status]: newValue,
		});
	};

	// Toggle loved status
	const toggleLoved = async () => {
		await saveReview({
			rating: review?.rating,
			content: review?.content || undefined,
			loved: !review?.loved,
			sawLive: review?.sawLive,
			sawStream: review?.sawStream,
			wantToSee: review?.wantToSee,
			mustSee: review?.mustSee,
		});
	};

	// Delete review (immediate — deliberate user action)
	const deleteReview = async () => {
		await saveReview({
			rating: 0,
			content: "",
			loved: false,
			sawLive: false,
			sawStream: false,
			wantToSee: false,
			mustSee: false,
		}, { immediate: true });
	};

	return {
		review,
		saveReview,
		deleteReview,
		toggleStatus,
		toggleLoved,
		isLoading,
		isPending: saveReviewMutation.isPending,
	};
}
